import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Cpu, Users, Trash2, UserPlus, CheckCircle2, XCircle, Clock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../auth/AuthContext";
import {
  twofaSetup, twofaEnable, twofaDisable, getAiProviders,
  listUsers, createUser, deleteUser, approveUser, suspendUser,
  paymentsHistory, errText,
} from "../../api";

export default function SecurityPanel({ snapshot, onCreatePaidUser }) {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const isTrader = user?.role === "trader";
  const [tab, setTab] = useState("2fa");

  const tabs = [
    ["2fa", "2FA"],
    ["ai", "Provider AI"],
    isAdmin ? ["users", "Utenti & Approvazioni"] : null,
    isTrader ? ["clienti", "Clienti & Pagamenti"] : null,
  ].filter(Boolean);

  return (
    <div id="impostazioni-sicurezza" data-testid="security-panel" className="panel p-5 float-in">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
        <span className="w-6 h-6 rounded-md grid place-items-center bg-emerald-500/15 text-emerald-400">
          <ShieldCheck size={13} />
        </span>
        Sicurezza, Accessi & Abbonamenti
      </div>

      <div className="flex flex-wrap gap-1 bg-[#0F1728] border border-white/10 rounded-lg p-1 mb-4 w-fit">
        {tabs.map(([id, label]) => (
          <button key={id} data-testid={`security-tab-${id}`} onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
              tab === id ? "bg-amber-500/20 text-amber-400" : "text-slate-400 hover:text-slate-200"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "2fa" && <TwoFactor user={user} refreshUser={refreshUser} />}
      {tab === "ai" && <AiProviders />}
      {tab === "users" && isAdmin && <UserManager currentEmail={user.email} />}
      {tab === "clienti" && isTrader && <TraderClients onCreatePaidUser={onCreatePaidUser} />}
    </div>
  );
}

function TwoFactor({ user, refreshUser }) {
  const [data, setData] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = user?.twoFactorEnabled;

  const start = async () => { setBusy(true); try { setData(await twofaSetup()); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const enable = async () => { try { await twofaEnable(otp); toast.success("2FA attivato"); setData(null); setOtp(""); refreshUser(); } catch (e) { toast.error(errText(e)); } };
  const disable = async () => { const code = window.prompt("Codice 2FA per disattivare"); if (!code) return; try { await twofaDisable(code); toast.success("2FA disattivato"); refreshUser(); } catch (e) { toast.error(errText(e)); } };

  if (enabled) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
        <div className="flex items-center gap-2 text-[12px] text-emerald-300"><CheckCircle2 size={16} /> 2FA <b>attiva</b> su questo account.</div>
        <button data-testid="twofa-disable-button" onClick={disable}
          className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md bg-rose-500/10 border border-rose-500/25 text-rose-300 hover:bg-rose-500/20">
          <ShieldOff size={13} /> Disattiva
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {!data ? (
        <button data-testid="twofa-setup-button" onClick={start} disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 disabled:opacity-60">
          <ShieldCheck size={14} /> Configura 2FA (TOTP)
        </button>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <div className="rounded-xl bg-white p-3 w-fit"><img src={data.qr} alt="QR 2FA" className="w-40 h-40" data-testid="twofa-qr-image" /></div>
          <div className="space-y-2.5">
            <p className="text-[11px] text-slate-300 leading-relaxed">Scansiona il QR con Google/Microsoft Authenticator e inserisci il codice.</p>
            <input data-testid="twofa-verify-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" inputMode="numeric"
              className="w-full bg-[#0A1220] border border-white/12 rounded-lg py-2.5 px-3 text-[14px] font-mono tracking-[0.3em] text-center outline-none focus:border-amber-500/50" />
            <button data-testid="twofa-verify-button" onClick={enable}
              className="w-full py-2.5 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-emerald-400 to-emerald-500">Verifica e attiva</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AiProviders() {
  const [data, setData] = useState(null);
  useEffect(() => { getAiProviders().then(setData).catch(() => {}); }, []);
  const providers = data?.providers || [];
  const configured = data?.configured;
  return (
    <div className="space-y-3">
      <div className={`flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg border ${configured ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-300" : "border-amber-500/20 bg-amber-500/5 text-amber-300"}`}>
        {configured ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
        {configured ? `Consenso AI reale attivo · ${providers.length} provider` : "Nessun provider · consenso AI simulato"}
      </div>
      {providers.map((p, i) => (
        <div key={i} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
          <div className="flex items-center gap-2 text-[12px] text-slate-200"><Cpu size={14} className="text-sky-400" /> {p.name}
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-sky-500/12 text-sky-400 uppercase">{p.kind}</span></div>
          <span className="text-[10px] font-mono text-slate-500">{p.model}</span>
        </div>
      ))}
      <p className="text-[10px] text-slate-500 leading-relaxed">
        Configura i provider (OpenAI, Anthropic, Ollama o endpoint OpenAI-compatibile) nelle variabili d'ambiente del server:
        <span className="font-mono"> AI_PROVIDER_1_URL / _MODEL / _KEY / _KIND</span>.
      </p>
    </div>
  );
}

const ROLE_BADGE = { admin: "bg-amber-500/15 text-amber-400", trader: "bg-sky-500/15 text-sky-400", readonly: "bg-slate-500/15 text-slate-400" };
const STATUS_BADGE = { approved: "bg-emerald-500/15 text-emerald-400", pending: "bg-amber-500/15 text-amber-400", suspended: "bg-rose-500/15 text-rose-400" };

function expiryLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (d.getFullYear() > 2090) return "illimitato";
  const days = Math.ceil((d - new Date()) / 86400000);
  return days > 0 ? `${days}g` : "scaduto";
}

function UserManager({ currentEmail }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", role: "trader", name: "", days: "" });
  const load = () => listUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (fn, ...args) => { try { await fn(...args); toast.success("Fatto"); load(); } catch (e) { toast.error(errText(e)); } };
  const add = async (e) => {
    e.preventDefault();
    try {
      await createUser({ ...form, days: form.days ? Number(form.days) : undefined });
      toast.success("Utente creato");
      setForm({ email: "", password: "", role: "trader", name: "", days: "" });
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-2">
        {users.map((u) => (
          <div key={u.email} data-testid={`user-row-${u.email}`} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[12px] text-slate-200">
              <Users size={14} className="text-slate-400" /> {u.email}
              {u.twoFactorEnabled && <ShieldCheck size={12} className="text-emerald-400" />}
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded uppercase ${ROLE_BADGE[u.role]}`}>{u.role}</span>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded uppercase ${STATUS_BADGE[u.status] || ""}`}>{u.status}</span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-slate-400 flex items-center gap-1"><Clock size={10} />{expiryLabel(u.accessExpiresAt)}</span>
              {u.status !== "approved" && (
                <button data-testid={`approve-user-${u.email}`} onClick={() => act(approveUser, u.email)}
                  className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20">Approva</button>
              )}
              {u.email !== currentEmail && u.status !== "suspended" && (
                <button data-testid={`suspend-user-${u.email}`} onClick={() => act(suspendUser, u.email)}
                  className="text-[10px] px-2 py-1 rounded bg-amber-500/10 border border-amber-500/25 text-amber-300 hover:bg-amber-500/20">Sospendi</button>
              )}
              {u.email !== currentEmail && (
                <button data-testid={`delete-user-${u.email}`} onClick={() => window.confirm(`Eliminare ${u.email}?`) && act(deleteUser, u.email)}
                  className="text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="grid sm:grid-cols-5 gap-2 items-end border-t border-white/8 pt-3">
        <input data-testid="new-user-email" required type="email" placeholder="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-2.5 text-[11px] outline-none focus:border-amber-500/50" />
        <input data-testid="new-user-password" required type="text" placeholder="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-2.5 text-[11px] outline-none focus:border-amber-500/50" />
        <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-2.5 text-[11px] outline-none focus:border-amber-500/50">
          <option value="admin">admin</option><option value="trader">trader</option><option value="readonly">readonly</option>
        </select>
        <input data-testid="new-user-days" type="number" placeholder="giorni" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-2.5 text-[11px] outline-none focus:border-amber-500/50" />
        <button data-testid="create-user-button" className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500"><UserPlus size={13} /> Crea</button>
      </form>
      <p className="text-[10px] text-slate-500">L'admin crea account (approvati) senza pagamento. I trader creano utenti a pagamento (10€/giorno).</p>
    </div>
  );
}

function TraderClients({ onCreatePaidUser }) {
  const [tx, setTx] = useState([]);
  useEffect(() => { paymentsHistory().then(setTx).catch(() => {}); }, []);
  return (
    <div className="space-y-4">
      <button data-testid="open-create-paid-user" onClick={onCreatePaidUser}
        className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400">
        <UserPlus size={14} /> Crea utente (10€/giorno × X)
      </button>
      <div className="grid gap-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><CreditCard size={12} /> Storico pagamenti</div>
        {tx.length === 0 ? (
          <div className="text-[11px] text-slate-500 py-3">Nessun pagamento registrato</div>
        ) : tx.map((t) => (
          <div key={t.orderId} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2 text-[11px]">
            <span className="text-slate-300">{t.purpose === "license" ? "Licenza" : `Utente ${t.newUser?.email || ""}`} · {t.days}g</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-amber-400">€ {Number(t.amount).toFixed(2)}</span>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded uppercase ${t.status === "completed" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{t.status}</span>
              {t.mock && <span className="text-[9px] text-slate-500">sim</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
